/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {Box, Badge} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useItemVariant} from '../item-variant'
import {FormattedMessage} from 'react-intl'
import {findImageGroupBy} from '@salesforce/retail-react-app/app/utils/image-groups-utils'
import DynamicImage from '@app/components/dynamic-image'
import Link from '@salesforce/retail-react-app/app/components/link'
import {getVariationGroupOfVariant} from '../../utils/product-utils'
import {useGTM} from '@app/hooks/use-gtm'

/**
 * In the context of a cart product item variant, this component renders the item's
 * main image.
 *
 * on the custom property `c_isSale`. This will need to be expanded upon to handle
 * different badge/qualifiers and property names.
 */
const ItemImage = ({imageProps, ...props}) => {
    const variant = useItemVariant()
    const productId = variant?.id
    const {triggerViewItemEvent} = useGTM()
    const variationGroup = getVariationGroupOfVariant(variant)

    // We find the 'front' images in the variant's image groups based on variationValues and pick the first one
    const image = findImageGroupBy(variant?.imageGroups, {
        viewType: 'front',
        selectedVariationAttributes: variant?.variationValues
    })?.images?.[0]

    const handleLinkClick = () => {
        triggerViewItemEvent('select_item', variant)
    }

    return (
        <Box {...props}>
            <Box position="relative">
                {variant?.c_isSale && (
                    <Badge
                        position="absolute"
                        top={0}
                        left={0}
                        marginLeft={2}
                        marginTop={2}
                        fontSize="10px"
                        variant="solid"
                        colorScheme="blue"
                    >
                        <FormattedMessage
                            defaultMessage="Sale"
                            id="item_image.label.sale"
                            description="A sale badge placed on top of a product image"
                        />
                    </Badge>
                )}
                {variant?.isProductUnavailable && (
                    <Badge
                        position="absolute"
                        top={0}
                        left={0}
                        marginLeft={2}
                        marginTop={2}
                        fontSize="10px"
                        variant="solid"
                        colorScheme="red"
                    >
                        <FormattedMessage
                            defaultMessage="Unavailable"
                            id="item_image.label.unavailable"
                            description="A unavailable badge placed on top of a product image"
                        />
                    </Badge>
                )}

                {image && (image?.disBaseLink || image?.link) && (
                    <Link
                        to={`/product/${variationGroup?.productId || productId}`}
                        onClick={handleLinkClick}
                        aria-label={variant?.productName || variant?.name}
                    >
                        <DynamicImage
                            src={`${image?.disBaseLink || image?.link}[?sw={width}&q=60]`}
                            widths={['384px', '384px', '400px', '360px', '480px', '505px']}
                            imageProps={{
                                alt: image?.alt,
                                loading: 'lazy',
                                ...imageProps
                            }}
                        />
                    </Link>
                )}
            </Box>
        </Box>
    )
}

ItemImage.propTypes = {
    imageProps: PropTypes.object,
    ratio: PropTypes.number
}

export default ItemImage
